/**
 * §6.2.2 — Keepa token bütçe yöneticisi.
 *
 * **"ZORUNLU — yoksa para yanar."** Keepa kullanım başına ücretlendiriyor ve
 * 80.451 ASIN'lik bir katalogda kontrolsüz istek aylık paketi bir günde
 * bitirebilir.
 *
 * Burada **iki ayrı sayaç** var ve karıştırılmamaları kritik:
 *
 * 1. **Günlük bütçe** (`dailyBudget` / `spentToday`) — bizim kendi kuralımız.
 *    Paketin günlük payını aşmamak için. Tier'lara buna göre izin veriyoruz.
 * 2. **Keepa kovası** (`tokensLeft` / `refillInMs`) — Keepa'nın anlık hız
 *    sınırı. Dakikada `tokensPerMinute` kadar doluyor, kullanılmayan token 60
 *    dakika sonra yanıyor.
 *
 * İkisi aynı şey değil: Starter pakette kova ~1.200 token taşırken günlük pay
 * ~28.800. `dailyBudget - tokensLeft` gibi bir hesap günlük harcamayı ilk
 * senkronda tavana vurdurur ve tüm senkronizasyonu kilitlerdi.
 *
 * Modül saf: veritabanı ve ağ bilmiyor, yalnızca karar veriyor.
 */

import type { TierName as Tier } from '../types/index.js';

export type { TierName } from '../types/index.js';

/** İşin aciliyeti — kullanıcının elle tetiklediği işler bekletilmez. */
export type JobUrgency = 'URGENT' | 'NORMAL';

/**
 * Token'ın ne için harcandığı.
 *
 * Bütçe paylaşımlı olduğu için sıralama tek başına yetmiyor: sabah başlatılan
 * 100.000'lik bir arama, envanter takibi daha uyanmadan günlük bütçeyi
 * tüketebilir. O gün 200.000 üründeki stok değişimleri kaçar — yani en kritik
 * iş, en az kritik iş yüzünden durur.
 *
 * Bu yüzden keşfin **payı** sınırlı.
 */
export type TokenPurpose = 'INVENTORY' | 'DISCOVERY';

/**
 * §6.2.2 — Keşfin kullanabileceği günlük bütçe payı.
 *
 * Yarı yarıya: keşif de kullanıcının işi, tamamen kısılmamalı. Ama envanter
 * takibinin payı garanti — stokta olmayan ürünü satmak, yeni ürün bulamamaktan
 * çok daha pahalı.
 */
export const DISCOVERY_BUDGET_SHARE = 0.5;

export interface BudgetState {
  /** Günlük token payı (bizim kuralımız). */
  dailyBudget: number;
  /** Bugün harcanan. */
  spentToday: number;
  /** Bugün keşif için harcanan (`spentToday`'in bir parçası). */
  spentTodayDiscovery: number;
  /** Rezerve edilmiş ama henüz harcanmamış. */
  reserved: number;
  /** Keepa kovasının anlık bakiyesi — bilinmiyorsa null. */
  tokensLeft: number | null;
  /** Kovanın dolmasına kalan süre (ms) — Keepa yanıtındaki `refillIn`. */
  refillInMs: number | null;
  /** Kovanın dakikada dolum hızı — paket seviyesinden gelir. */
  tokensPerMinute: number;
  /** Sayaçların hangi güne ait olduğu (UTC, `YYYY-MM-DD`). */
  dayKey: string | null;
  /** Kovanın Keepa ile en son ne zaman eşitlendiği — yerel dolum bundan sayılıyor. */
  lastSyncedAt: Date | null;
  /**
   * §11.3 — Öncelikli katmanların **günlük gerçek jeton ihtiyacı**.
   *
   * Satan ürünlere ayrılacak pay buradan geliyor; bilinmiyorsa `null` ve
   * eski sabit yüzdelere düşülüyor (aşağıda `budgetLevel`).
   */
  hotWarmDailyNeed?: number | null;
  /** Yalnızca HOT'un günlük ihtiyacı — CRITICAL eşiği için. */
  hotDailyNeed?: number | null;
}

export type BudgetLevel = 'NORMAL' | 'TIGHT' | 'CRITICAL' | 'EXHAUSTED';

/**
 * §6.2.2 — Günlük bütçe doluluk oranı (0-1).
 *
 * Rezervasyonlar da harcanmış sayılıyor: aynı anda çalışan işler bütçenin aynı
 * kısmını iki kez kullanamamalı.
 */
export function budgetUsage(state: BudgetState): number {
  if (state.dailyBudget <= 0) return 1;
  return Math.min(1, (state.spentToday + state.reserved) / state.dailyBudget);
}

/** Talep bilinmediğinde kullanılan eski sabit eşikler. */
const SABIT_TIGHT_ESIK = 0.8;
const SABIT_CRITICAL_ESIK = 0.95;

/**
 * Ayrılan paya güvenlik katsayısı.
 *
 * Katman sayıları gün içinde artabilir (bir ürün satarsa HOT olur). Payı
 * ihtiyacın tam üstüne oturtmak, o artışı karşılayamazdı.
 */
const PAY_GUVENLIK_KATSAYISI = 2;

/**
 * Ayrılan pay bütçenin bu oranını **aşamaz**.
 *
 * ⚠️ Tavan olmadan hesap kendi kuyruğunu yiyordu: talep bütçeden büyükse
 * (katalog büyüyünce oluyor — 200.000 üründe varsayılan oranlar 72.000 jeton
 * istiyor, bütçe 28.800) pay bütçenin tamamını kaplıyor, seviye kalıcı olarak
 * CRITICAL'a düşüyor ve HOT dışında hiçbir şey çalışmıyordu. Bu, eski sabit
 * yüzdelerden **daha kötü**: onlar hiç değilse bütçenin %80'ini herkese
 * açıyordu.
 *
 * Tavan, iki uçtan da koruyor: pay küçükken israfı önlüyor (asıl amaç),
 * pay büyükken de kuyruğun tamamen durmasını.
 */
const PAY_TAVANI = 0.25;

/**
 * §6.2.2 / §11.3 — Bütçe seviyesi.
 *
 * Seviye, **satan ürünlere ne kadar bütçe gerektiğinden** hesaplanıyor:
 *
 * · kalan > HOT+WARM ihtiyacı  → NORMAL    (her tier)
 * · kalan ≤ HOT+WARM ihtiyacı  → TIGHT     (yalnızca HOT + WARM)
 * · kalan ≤ HOT ihtiyacı       → CRITICAL  (yalnızca HOT + acil işler)
 * · kalan ≤ 0                  → EXHAUSTED (yalnızca acil)
 *
 * ⚠️ Burada bir dönem sabit yüzdeler vardı: %80'de TIGHT, %95'te CRITICAL.
 * Üretimde ölçüldü (28 Ağustos 2026): kullanıcının 89 HOT + 34 WARM ürünü
 * günde ~636 jetona ihtiyaç duyarken, %20'lik sabit pay 5.600 jeton bloke
 * ediyordu — ihtiyacın 9 katı. Keepa günde ~3 saat boş duruyor, ~4.900 jeton
 * kullanılmadan yanıyor ve 34.802 ürün sırada bekliyordu.
 *
 * Talep bilinmiyorsa (`null`) eski sabit yüzdelere düşülüyor: bilmeden
 * cömert davranmaktansa temkinli kalmak doğru.
 */
export function budgetLevel(state: BudgetState): BudgetLevel {
  const usage = budgetUsage(state);
  if (usage >= 1) return 'EXHAUSTED';

  const hotWarm = state.hotWarmDailyNeed;
  const hot = state.hotDailyNeed;
  if (hotWarm == null || hot == null) {
    if (usage >= SABIT_CRITICAL_ESIK) return 'CRITICAL';
    if (usage >= SABIT_TIGHT_ESIK) return 'TIGHT';
    return 'NORMAL';
  }

  const tavan = state.dailyBudget * PAY_TAVANI;
  const hamHot = hot * PAY_GUVENLIK_KATSAYISI;
  const hamHotWarm = hotWarm * PAY_GUVENLIK_KATSAYISI;

  /*
   * Tavana çarparken **ikisi birden** aynı oranda küçültülüyor.
   *
   * Her birini ayrı ayrı kırpmak ikisini de tavana eşitliyordu; o zaman
   * TIGHT bandı yok oluyor ve sistem NORMAL'den doğrudan CRITICAL'a
   * atlıyordu — yani WARM ürünler hiç ara kademe görmeden kesiliyordu.
   */
  const olcek = hamHotWarm > tavan && hamHotWarm > 0 ? tavan / hamHotWarm : 1;
  const hotPay = hamHot * olcek;
  const hotWarmPay = hamHotWarm * olcek;

  const kalan = state.dailyBudget - state.spentToday - state.reserved;
  if (kalan <= hotPay) return 'CRITICAL';
  if (kalan <= hotWarmPay) return 'TIGHT';
  return 'NORMAL';
}

/**
 * §11.3 — Bu bütçe seviyesinde hangi tier'lar çalışabilir?
 *
 * "Token yetmezse otomatik daraltma" kuralının karar tablosu.
 */
export function allowedTiers(state: BudgetState): Tier[] {
  switch (budgetLevel(state)) {
    case 'NORMAL':
      return ['HOT', 'WARM', 'NORMAL', 'COLD', 'FROZEN'];
    case 'TIGHT':
      return ['HOT', 'WARM'];
    case 'CRITICAL':
      return ['HOT'];
    case 'EXHAUSTED':
      return [];
  }
}

/**
 * Bu tier günlük bütçe kuralına göre şu an çalışabilir mi?
 *
 * Acil işler (kullanıcının elle tetiklediği "şimdi güncelle") bütçe seviyesinden
 * muaf: kullanıcı ekranın başında bekliyorsa cevapsız bırakmak birkaç token'dan
 * pahalıya gelir. Kova boşsa acil iş de bekler — orası Keepa'nın kuralı, bizim
 * değil.
 */
export function tierAllowed(
  state: BudgetState,
  tier: Tier,
  urgency: JobUrgency = 'NORMAL',
): boolean {
  if (urgency === 'URGENT') return true;
  return allowedTiers(state).includes(tier);
}

/**
 * Reddin hangi sınıra takıldığı — bekleme süresini çağıran buna göre seçiyor.
 *
 * `BUCKET` dakikalık hız sınırı: saniyeler içinde kendi kendine açılıyor,
 * `retryAfterMs` zaten doğru süreyi veriyor.
 *
 * `DAILY` günlük bütçe kapısı: `spentToday` yalnızca artıyor, yani bu kapı
 * gün dönene kadar açılmıyor. 60 saniyede bir tekrar denemek boşuna — üretimde
 * ölçüldü (7 Eylül 2026): bütçe %75'i geçtikten sonra 30 dakikada **8.843**
 * iş aynı sebeple ertelendi. Çağıran bunu gün dönümüne kadar bekletmeli.
 */
export type RetryScope = 'BUCKET' | 'DAILY' | 'INVALID';

export interface Reservation {
  ok: boolean;
  /** Rezerve edilen token. */
  tokens: number;
  /** Reddedildiyse neden — kullanıcıya ve loga gider. */
  reason: string | null;
  /** Ne kadar sonra tekrar denenmeli (ms)? */
  retryAfterMs: number | null;
  /** Reddedildiyse hangi sınıra takıldı? Başarılıysa `null`. */
  retryScope: RetryScope | null;
  /** Rezervasyon işlenmiş yeni durum. */
  state: BudgetState;
}

/** Kova dolana kadar ne kadar beklenmeli? */
function refillWaitMs(state: BudgetState, needed: number): number {
  const have = state.tokensLeft ?? 0;
  const missing = Math.max(0, needed - have);
  if (missing === 0) return 0;
  const perMinute = state.tokensPerMinute > 0 ? state.tokensPerMinute : 1;
  const byRate = Math.ceil((missing / perMinute) * 60_000);
  // Keepa kendi tahminini verdiyse ikisinden büyüğünü al — erken dönüp
  // boşuna istek atmaktansa biraz fazla beklemek iyidir
  return Math.max(byRate, state.refillInMs ?? 0);
}

/**
 * Kovanın maksimum kapasitesi — bir saatlik dolum.
 *
 * Keepa kovayı sınırsız biriktirmiyor; üst sınır olmadan uzun bir sessizlikten
 * sonra elimizde gerçekte olmayan devasa bir bakiye varmış gibi görünürdü ve
 * ilk turda arka arkaya reddedilen istekler atardık.
 */
const KOVA_KAPASITE_DAKIKA = 60;

/**
 * §6.2.2 — Kovayı geçen süreye göre yerel olarak doldurur.
 *
 * ⚠️ Bu olmadan sistem **kalıcı olarak kilitleniyordu.** Kova bir kez sıfıra
 * indiğinde her rezervasyon reddediliyor; istek atılmayınca Keepa'dan yanıt
 * gelmiyor; yanıt gelmeyince `tokensLeft` hiç güncellenmiyor. Kullanıcının
 * kurulumunda tam olarak bu oldu: kova sıfırlandıktan sonra 19 dakika boyunca
 * tek bir Keepa isteği gitmedi, kaynak takibi sessizce tarayıcıya düştü.
 *
 * Tahmin ihtiyatlı: Keepa gerçek bakiyeyi bildirdiğinde `syncWithKeepa` onu
 * ezer (o kazanır). Fazla tahmin edersek Keepa 429 döner ve devre kesici
 * devreye girer; eksik tahmin edersek yalnızca biraz yavaş çalışırız.
 */
export function refillByElapsed(state: BudgetState, now: Date): BudgetState {
  if (state.tokensLeft === null || state.lastSyncedAt === null) return state;
  if (state.tokensPerMinute <= 0) return state;

  const gecenMs = now.getTime() - state.lastSyncedAt.getTime();
  if (gecenMs <= 0) return state;

  const kazanilan = Math.floor((gecenMs / 60_000) * state.tokensPerMinute);
  if (kazanilan <= 0) return state;

  const kapasite = state.tokensPerMinute * KOVA_KAPASITE_DAKIKA;
  return {
    ...state,
    tokensLeft: Math.min(kapasite, state.tokensLeft + kazanilan),
  };
}

/**
 * §6.2.2 — İstek öncesi rezervasyon.
 *
 * Rezervasyon **istekten önce** alınıyor: yanıt gelene kadar bütçenin o kısmı
 * başka işe verilmiyor. Yoksa aynı anda çalışan on iş bütçeyi aşar ve fazlası
 * ya reddedilir ya da — daha kötüsü — ücretlendirilir.
 *
 * Dönen `state` çağıranın saklaması gereken yeni durum; `commit()` veya
 * `release()` ile mutlaka kapatılmalı.
 */
export function reserve(
  state: BudgetState,
  tokens: number,
  tier: Tier,
  urgency: JobUrgency = 'NORMAL',
  purpose: TokenPurpose = 'INVENTORY',
): Reservation {
  const deny = (
    reason: string,
    retryAfterMs: number | null,
    retryScope: RetryScope,
  ): Reservation => ({
    ok: false,
    tokens: 0,
    reason,
    retryAfterMs,
    retryScope,
    state,
  });

  if (!Number.isFinite(tokens) || tokens <= 0) {
    return deny('Token miktarı sıfırdan büyük olmalı', null, 'INVALID');
  }

  if (!tierAllowed(state, tier, urgency)) {
    const level = budgetLevel(state);
    return deny(
      level === 'EXHAUSTED'
        ? 'Günlük Keepa bütçesi doldu. Yarın sıfırlanacak veya bütçeyi artırın.'
        : `Keepa bütçesi %${Math.round(budgetUsage(state) * 100)} doluluğa ulaştı; ` +
            `${tier} öncelikli işler şimdilik bekletiliyor.`,
      // Bütçe gün dönümünde sıfırlanıyor; süreyi çağıran saate göre seçiyor
      60_000,
      'DAILY',
    );
  }

  /*
   * Keşif payı — envanter takibinin bütçesi garanti altında.
   *
   * Acil işler (kullanıcı ekranın başında bekliyor) bu paydan muaf; onlar
   * zaten sınırlı sayıda.
   */
  if (purpose === 'DISCOVERY' && urgency !== 'URGENT') {
    const share = Math.floor(state.dailyBudget * DISCOVERY_BUDGET_SHARE);
    if (state.spentTodayDiscovery + tokens > share) {
      return deny(
        `Ürün araması bugünkü payını doldurdu (${share.toLocaleString('tr-TR')} token). ` +
          'Envanter takibinin bütçesi korunuyor; arama yarın kaldığı yerden devam edecek.',
        60_000,
        'DAILY',
      );
    }
  }

  const remaining = state.dailyBudget - state.spentToday - state.reserved;
  if (remaining < tokens) {
    return deny(
      `Kalan günlük bütçe ${Math.max(0, remaining)} token; bu iş ${tokens} token istiyor.`,
      60_000,
      'DAILY',
    );
  }

  // Keepa kovası — hız sınırı. Bütçe uygun olsa da kova boşsa istek atılamaz.
  if (state.tokensLeft !== null && state.tokensLeft < tokens) {
    const wait = refillWaitMs(state, tokens);
    return deny(
      `Keepa kovasında ${state.tokensLeft} token var, bu iş ${tokens} istiyor. ` +
        `Kova dakikada ${state.tokensPerMinute} token doluyor.`,
      wait,
      'BUCKET',
    );
  }

  return {
    ok: true,
    tokens,
    reason: null,
    retryAfterMs: null,
    retryScope: null,
    state: { ...state, reserved: state.reserved + tokens },
  };
}

/**
 * İstek başarıyla tamamlandı — rezervasyon harcamaya dönüşüyor.
 *
 * `actualTokens` verilirse gerçek maliyet kullanılır: Keepa'nın bir isteğe kaç
 * token saydığı tahminimizden farklı çıkabiliyor (teklif sayfası sayısına göre
 * değişiyor).
 */
export function commit(
  state: BudgetState,
  reservedTokens: number,
  actualTokens?: number,
  purpose: TokenPurpose = 'INVENTORY',
): BudgetState {
  const spent = Math.max(0, actualTokens ?? reservedTokens);
  return {
    ...state,
    reserved: Math.max(0, state.reserved - reservedTokens),
    spentToday: state.spentToday + spent,
    spentTodayDiscovery:
      purpose === 'DISCOVERY' ? state.spentTodayDiscovery + spent : state.spentTodayDiscovery,
    tokensLeft: state.tokensLeft === null ? null : Math.max(0, state.tokensLeft - spent),
  };
}

/** İstek atılamadı — rezervasyon geri veriliyor, hiçbir şey harcanmadı. */
export function release(state: BudgetState, reservedTokens: number): BudgetState {
  return { ...state, reserved: Math.max(0, state.reserved - reservedTokens) };
}

/**
 * §6.2.2 — Keepa yanıtındaki gerçek kova bakiyesiyle senkronizasyon.
 *
 * Kova hakkında bizim tahminimiz yaklaşıktır: bir isteğin kaç token yaktığını
 * tam bilemeyiz ve aynı anahtarı başka bir uygulama kullanıyor olabilir. Keepa
 * `tokensLeft` bildirdiğinde **o kazanır**.
 *
 * Günlük harcama sayacına dokunulmuyor — o bizim kendi defterimiz ve Keepa'nın
 * kova bakiyesinden türetilemez.
 */
export function syncWithKeepa(
  state: BudgetState,
  response: { tokensLeft: number; refillIn?: number },
): BudgetState {
  return {
    ...state,
    tokensLeft: Math.max(0, response.tokensLeft),
    refillInMs: response.refillIn ?? null,
  };
}

/**
 * Gün dönümünde sayaçları sıfırlar.
 *
 * Keepa günlük payı UTC gününe göre sayılıyor; yerel saate göre hesaplamak
 * kullanıcının gecesinde bütçeyi erken ya da geç sıfırlardı.
 *
 * Rezervasyonlar taşınıyor: gün dönerken uçuşta olan istekler var ve onların
 * karşılığı hâlâ tutulmalı.
 */
export function rollDay(state: BudgetState, now: Date): BudgetState {
  const today = dayKey(now);
  if (state.dayKey === today) return state;
  return { ...state, dayKey: today, spentToday: 0, spentTodayDiscovery: 0 };
}

/**
 * §6.2.3 — Tier'ın ASIN başına token maliyeti.
 *
 * **Hepsi 1 token.** Spesifikasyondaki tablo HOT/WARM için teklif listesi
 * (`offers=20&stock=1`) öngörüyor ve bu +6 token demek — ama o listeyi
 * kullandığımız bir yer yok:
 *
 * · Ürünü zaten BuyBox'ı kazanan satıcıdan alıyoruz; .com'daki diğer 19
 *   satıcı bizi ilgilendirmiyor.
 * · Rakip analizi **Japonya tarafında** ve o veri SP-API'den bedava geliyor (§12).
 * · Stokta "var/yok" bilgisi `availabilityAmazon` ve BuyBox fiyatının
 *   varlığından çıkıyor; satıcı başına adet bilgisine ihtiyacımız yok.
 *
 * Kullanıcı kararı (24 Ağustos 2026): okunmayan liste için 7 kat ödeme
 * kaldırıldı. Fatura 855.000 → 160.000 token/gün.
 *
 * ⚠️ **ASIN başına 3 token — varsayım değil, ölçüm.**
 *
 * Burada 1 yazıyordu; gerekçe "`stats` ve `buybox` ek token istemiyor"du.
 * Üretimde ölçülen bunu yalanladı:
 *
 *     Keepa product isteği 255 token harcadı, 85 tahmin edilmişti
 *
 * 85 ASIN → 255 token, yani tam 3 katı. Gerçek harcama zaten doğru
 * kaydediliyordu (`commit` tahmini değil `consumed`'ı yazıyor), dolayısıyla
 * fatura sürprizi yok. Yanlış olan **ileriye dönük** hesaptı: planlayıcı bir
 * turda sığandan 3 kat fazla ASIN planlıyor, katman aralığı 3 kat kısa
 * çıkıyordu. Sonuç, bütçenin gün ortasında bitmesi ve katalogun kuyruğunun
 * hiç okunmaması olurdu.
 *
 * Farkı **`buybox` yaratıyor**, üretimde iki profil yan yana ölçüldü:
 *
 *     Keepa grubu işlendi: 5/5 ürün, 15 token (WARM)    → stats + buybox = 3
 *     Keepa grubu işlendi: 4/4 ürün,  4 token (FROZEN)  → stats        = 1
 *
 * Yani `stats` bedava, `buybox` +2 token.
 *
 * ⭐ **30 Ağustos 2026 — artık HER katman 3 token.**
 *
 * Ucuz katman `buybox` istemediği için stok da getirmiyordu; ölçüm:
 * FROZEN'da stok doluluğu %3,0, HOT'ta %98,8. Kullanıcı kendi tarayıcısını
 * bırakıp yalnızca Keepa'ya geçtiğinden stoğun tek kaynağı bu istek — ucuz
 * katman artık bir seçenek değil (bkz. `TIER_REQUEST_PROFILE`).
 *
 * Bedeli açık ve kabul edildi: günlük kapasite tek abonelikte 28.800'den
 * 9.600 ürüne iniyor. Karşılığında ikinci abonelik alınıyor (40 token/dk →
 * 19.200 ürün/gün) ve her üründe stok, buybox, rakip sayısı geliyor.
 *
 * Teklif listesi (`offers=20`, +6 token) hâlâ çekilmiyor — ADR-075 yerinde.
 */
/**
 * §6.2.1 — Bir ürün isteğinin jeton bileşenleri.
 *
 * Ölçülmüş (30 Ağustos 2026, üretim): stats-only 1,00 · stats+buybox 3,00.
 */
export const KEEPA_TEMEL_JETON = 1;
export const KEEPA_BUYBOX_EK_JETON = 2;
/** Teklif listesi (`offers=20`) — ADR-075 gereği hiç istenmiyor. */
export const KEEPA_OFFERS_EK_JETON = 6;

/**
 * ⭐ İstek seçeneklerinden gerçek jeton maliyeti.
 *
 * Bir dönem maliyet KATMANDAN türüyordu (`TIER_TOKEN_COST[tier]`) ve istek
 * profiliyle arasında kodda hiçbir bağ yoktu: `buybox`'ı kapatmak rezervasyonu
 * düşürmüyor, açmak da artırmıyordu. Muhasebe ile gerçek istek birbirinden
 * bağımsız iki yerde tanımlıydı; biri değişince öteki sessizce yanlış oluyordu.
 */
export function jetonMaliyeti(secenekler: {
  buybox?: boolean;
  offers?: number;
}): number {
  return (
    KEEPA_TEMEL_JETON +
    (secenekler.buybox === true ? KEEPA_BUYBOX_EK_JETON : 0) +
    (typeof secenekler.offers === 'number' && secenekler.offers > 0 ? KEEPA_OFFERS_EK_JETON : 0)
  );
}

/**
 * ⭐ **Toplu envanter taramasında `buybox` isteniyor mu?**
 *
 * Kapalıyken ürün başına 1, açıkken 3 jeton. 93.611 ürünlük katalogda düz
 * rotasyon için fark, tam turun **üç katı** demek.
 *
 * Kapatmayı mümkün kılan şey: stok artık yalnızca `stats.buyBoxPrice`'tan
 * türetilmiyor. `extractStock` temel yanıtta zaten bulunan alanlara da bakıyor
 * (COUNT_NEW, NEW, NEW_FBA, AMAZON) — bkz. `keepa-mapper.ts`.
 *
 * ⚠️ Sipariş öncesi kaynak kontrolü (`order-source-check`) bu bayrağı
 * DİNLEMİYOR; orada hep buybox isteniyor. Sipariş gelmiş bir üründe stok
 * yanılgısı gerçek paraya mal olur ve o çağrılar günde bir avuç.
 */
export const TOPLU_TARAMADA_BUYBOX =
  (globalThis as { process?: { env?: Record<string, string | undefined> } }).process?.env?.[
    'KEEPA_BULK_BUYBOX'
  ] === '1';

export const TIER_TOKEN_COST: Record<Tier, number> = (() => {
  const c = jetonMaliyeti({ buybox: TOPLU_TARAMADA_BUYBOX });
  return { HOT: c, WARM: c, NORMAL: c, COLD: c, FROZEN: c };
})();

/** Yeni ürün keşfi: US + JP iki domain sorgusu (§6.2.3). */
export const DISCOVERY_TOKEN_COST = 2;

/** Satıcı profili sorgusu (§6.2.5). */
export const SELLER_TOKEN_COST = 1;

/** Keepa isteği başına en fazla ASIN (§6.2.1). */
export const MAX_ASINS_PER_REQUEST = 100;

/** Bir grup ASIN'in bu tier'da kaça mal olacağı. */
export function estimateTokens(tier: Tier, asinCount: number): number {
  return TIER_TOKEN_COST[tier] * Math.max(0, asinCount);
}

/**
 * §11.4 — Planlayıcının bu turda kaç ASIN alabileceği.
 *
 * `min(kalan_token / ortalama_token_maliyeti, 5000)`. Kova da sınırlıyor: kovada
 * olmayan token'a iş planlamak, işleri kuyrukta bekletmekten başka bir şey
 * yapmaz.
 */
export function schedulerBatchSize(
  state: BudgetState,
  tier: Tier,
  hardCap = 5_000,
): number {
  if (!tierAllowed(state, tier)) return 0;
  const perAsin = TIER_TOKEN_COST[tier];
  const budgetLeft = Math.max(0, state.dailyBudget - state.spentToday - state.reserved);
  const bucketLeft = state.tokensLeft ?? budgetLeft;
  const usable = Math.min(budgetLeft, bucketLeft);
  return Math.max(0, Math.min(hardCap, Math.floor(usable / perAsin)));
}

/** Keşif için bugün kalan token — ekranda gösteriliyor. */
export function discoveryRemaining(state: BudgetState): number {
  const share = Math.floor(state.dailyBudget * DISCOVERY_BUDGET_SHARE);
  const budgetLeft = Math.max(0, state.dailyBudget - state.spentToday - state.reserved);
  return Math.max(0, Math.min(share - state.spentTodayDiscovery, budgetLeft));
}

/** Kullanıcıya gösterilecek Türkçe seviye adı (§19). */
export const BUDGET_LEVEL_LABELS: Record<BudgetLevel, string> = {
  NORMAL: 'Normal',
  TIGHT: 'Bütçe daraldı',
  CRITICAL: 'Bütçe kritik',
  EXHAUSTED: 'Bütçe doldu',
};

export const BUDGET_LEVEL_TONE: Record<BudgetLevel, 'success' | 'warning' | 'danger'> = {
  NORMAL: 'success',
  TIGHT: 'warning',
  CRITICAL: 'danger',
  EXHAUSTED: 'danger',
};

/** UTC gün anahtarı — günlük sayaçların ne zaman sıfırlanacağını belirler. */
export function dayKey(now: Date): string {
  return now.toISOString().slice(0, 10);
}

/** UTC ay anahtarı — aylık kullanım göstergesi için (§6.2.2). */
export function monthKey(now: Date): string {
  return now.toISOString().slice(0, 7);
}
